function toNonEmpty(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function isNumericSiteRef(value) {
  return /^\d+$/.test(String(value || "").trim());
}

function normalizePublicSiteToken(value) {
  const token = toNonEmpty(value);
  if (!token || isNumericSiteRef(token)) return null;
  return token;
}

function readSiteTokenFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "");
    return normalizePublicSiteToken(params.get("siteId"));
  } catch {
    return null;
  }
}

import { resolvePublicBaseUrl } from "./resolvePublicBaseUrl";
import {
  readRuntimePublicSiteIdentity,
  resolvePublicSiteBootstrapCandidate,
} from "../features/vacationPackagesPublic/publicSiteIdentity";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import { rewritePublicHrefForCurrentSurface } from "../surfaces/tripMaster/tripMasterUrls";

function readPersistedSiteToken(now = Date.now()) {
  if (typeof window === "undefined") return null;
  try {
    const rawIdentity = window.localStorage?.getItem("hr_public_site_identity_v1");
    if (rawIdentity) {
      const parsed = JSON.parse(rawIdentity);
      const token = normalizePublicSiteToken(parsed?.publicSiteToken);
      const expiresAt = Number(parsed?.expiresAt);
      if (token && Number.isFinite(expiresAt) && expiresAt > now) return token;
    }
    return normalizePublicSiteToken(
      window.localStorage?.getItem("hr_agent_public_site_token") ||
        window.sessionStorage?.getItem("hr_agent_public_site_token")
    );
  } catch {
    return null;
  }
}

function readSiteTokenFromRuntimePublicContext() {
  if (typeof window === "undefined") return null;
  try {
    const runtimeIdentity = readRuntimePublicSiteIdentity();
    if (runtimeIdentity?.publicSiteToken) {
      return normalizePublicSiteToken(runtimeIdentity.publicSiteToken);
    }
    const candidate = resolvePublicSiteBootstrapCandidate({
      search: window.location?.search || "",
      pathname: window.location?.pathname || "",
      hostname: window.location?.hostname || "",
    });
    if (String(candidate?.source || "") === "localStorage") return null;
    return normalizePublicSiteToken(candidate?.siteRef, candidate?.persistedIdentity?.publicSiteToken);
  } catch {
    return null;
  }
}

function resolveTokenForAppend(explicitToken, options = {}) {
  return (
    normalizePublicSiteToken(explicitToken) ||
    readSiteTokenFromSearch(options.search) ||
    readSiteTokenFromSearch(options.currentSearch) ||
    normalizePublicSiteToken(options.currentPathToken) ||
    normalizePublicSiteToken(options.currentHostToken) ||
    readSiteTokenFromSearch(typeof window !== "undefined" ? window.location.search : "") ||
    readSiteTokenFromRuntimePublicContext() ||
    readPersistedSiteToken()
  );
}

function appendTokenToSearch(search, token) {
  const params = new URLSearchParams(search || "");
  params.set("siteId", token);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function appendPublicSiteToken(to, publicSiteToken = null, options = {}) {
  if (isTripMasterSurface()) {
    return rewritePublicHrefForCurrentSurface(to);
  }
  const token = resolveTokenForAppend(publicSiteToken, options);
  if (!token) return to;

  if (to && typeof to === "object") {
    return {
      ...to,
      search: appendTokenToSearch(to.search || "", token),
    };
  }

  const raw = String(to || "");
  const hashIndex = raw.indexOf("#");
  const withoutHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
  const hash = hashIndex >= 0 ? raw.slice(hashIndex) : "";
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex) : "";
  return `${pathname}${appendTokenToSearch(search, token)}${hash}`;
}

function normalizeOrigin(rawOrigin) {
  const fallback = "http://localhost:8000";
  const origin = toNonEmpty(rawOrigin) || fallback;
  try {
    const url = new URL(origin);
    return `${url.protocol}//${url.host}`;
  } catch {
    return fallback;
  }
}

function normalizeHttpUrl(rawUrl, protocolHint) {
  const url = toNonEmpty(rawUrl);
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const protocol = protocolHint === "https:" ? "https://" : "http://";
  return `${protocol}${url.replace(/^\/+/, "")}`;
}

function normalizeAgentId(value) {
  const raw = toNonEmpty(value);
  if (!raw) return null;
  const text = String(raw).trim();
  return /^\d+$/.test(text) ? text : null;
}

function buildVacationPackagesPublicSiteUrl(origin, siteRef) {
  const ref = toNonEmpty(siteRef);
  const qs = ref ? `?siteId=${encodeURIComponent(ref)}` : "";
  return `${origin}/vacation-packages${qs}`;
}

function resolveAgentPublicIdentitySeeds(agent) {
  const source = agent && typeof agent === "object" ? agent : {};
  const publicSiteId = toNonEmpty(
    source.public_site_id ||
      source.publicSiteId ||
      source.site_id ||
      source.siteId
  );
  const publicSlug = toNonEmpty(
    source.public_site_slug || source.publicSiteSlug || source.slug
  );
  const publicToken = normalizePublicSiteToken(
    source.public_site_token ||
      source.publicSiteToken ||
      source.site_token ||
      source.siteToken ||
      publicSiteId
  );
  return {
    publicSiteId,
    publicSlug,
    publicToken,
    publicTokenExists: Boolean(publicToken),
    agentId: normalizeAgentId(source.id || source.agent_id || source.agentId),
    agentEmail: toNonEmpty(source.email),
  };
}

export function buildPublicSiteUrl(agent, options = {}) {
  const source = agent && typeof agent === "object" ? agent : {};
  const explicitOrigin =
    options && Object.prototype.hasOwnProperty.call(options, "origin")
      ? options.origin
      : null;
  const origin = normalizeOrigin(
    explicitOrigin != null && explicitOrigin !== ""
      ? explicitOrigin
      : resolvePublicBaseUrl({ skipTrace: true })
  );
  const parsedOrigin = new URL(origin);
  const protocolHint = parsedOrigin.protocol;

  const explicitUrl = normalizeHttpUrl(
    source.public_site_url || source.publicSiteUrl,
    protocolHint
  );
  if (explicitUrl) return explicitUrl;

  const explicitDomain = toNonEmpty(source.public_site_domain || source.publicSiteDomain);
  if (explicitDomain) {
    return normalizeHttpUrl(explicitDomain, protocolHint);
  }

  const siteSlug = toNonEmpty(source.public_site_slug || source.publicSiteSlug);
  const siteToken = normalizePublicSiteToken(
    source.public_site_token ||
      source.publicSiteToken ||
      source.public_site_id ||
      source.publicSiteId
  );
  const siteRef = siteSlug || siteToken;
  return buildVacationPackagesPublicSiteUrl(origin, siteRef);
}

export async function buildAgentPublicSiteUrl(agent, options = {}) {
  const source = agent && typeof agent === "object" ? agent : {};
  const explicitOrigin =
    options && Object.prototype.hasOwnProperty.call(options, "origin")
      ? options.origin
      : null;
  const origin = normalizeOrigin(
    explicitOrigin != null && explicitOrigin !== ""
      ? explicitOrigin
      : resolvePublicBaseUrl({ skipTrace: true })
  );
  const parsedOrigin = new URL(origin);
  const protocolHint = parsedOrigin.protocol;
  const seeds = resolveAgentPublicIdentitySeeds(source);
  const resolveByAgentId = options?.resolveByAgentId;

  const explicitUrl = normalizeHttpUrl(
    source.public_site_url || source.publicSiteUrl,
    protocolHint
  );
  if (explicitUrl) {
    return { generatedUrl: explicitUrl, source: "public_site_url", ...seeds };
  }

  const customDomain = normalizeHttpUrl(
    source.public_site_domain || source.publicSiteDomain,
    protocolHint
  );
  if (customDomain) {
    return { generatedUrl: customDomain, source: "custom_domain", ...seeds };
  }

  if (seeds.publicSlug) {
    return {
      generatedUrl: buildVacationPackagesPublicSiteUrl(origin, seeds.publicSlug),
      source: "slug_query",
      ...seeds,
    };
  }

  if (seeds.publicToken) {
    return {
      generatedUrl: buildVacationPackagesPublicSiteUrl(origin, seeds.publicToken),
      source: "public_token_query",
      ...seeds,
    };
  }

  if (typeof resolveByAgentId === "function" && seeds.agentId) {
    try {
      const resolved = await resolveByAgentId(seeds.agentId);
      if (resolved?.success && resolved?.siteRef) {
        const resolvedSlug = toNonEmpty(resolved.resolvedSlug);
        const resolvedToken = normalizePublicSiteToken(resolved.siteRef);
        const siteRefForUrl = resolvedToken || resolvedSlug;
        const generatedUrl = siteRefForUrl
          ? buildVacationPackagesPublicSiteUrl(origin, siteRefForUrl)
          : null;
        return {
          generatedUrl,
          source: generatedUrl ? "agent_id_lookup_token" : "missing_identity",
          ...seeds,
          publicSlug: resolvedSlug || seeds.publicSlug || null,
          publicSiteId: toNonEmpty(resolvedToken || seeds.publicSiteId),
          publicToken: resolvedToken,
          publicTokenExists: Boolean(resolvedToken),
          agentId: normalizeAgentId(resolved.resolvedAgentId || seeds.agentId),
        };
      }
    } catch {
      // no-op, return missing identity below
    }
  }

  return { generatedUrl: null, source: "missing_identity", ...seeds };
}

